import React, { Activity } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import { styled } from 'sensortower-components/src/utils/styled';
import { RichText } from 'sensortower-components/src/base-components/Text';
import Header, { HeaderProps } from 'sensortower-components/src/base-components/Header';

import Footer, { FooterProps } from '../../components/Footer/Footer';
import Text from '../../components/Text';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import { sidekick } from '../../utils/sidekick';
import { useLocalizationContext } from '../../components/LocalizationContext';

export interface PressProps {
  sidekickLookup: any;
  title?: string;
  summary?: string;
  body?: RichText;
  header?: HeaderProps;
  footer?: FooterProps;
}

export const Press = ({ sidekickLookup, title, summary, body, header, footer }: PressProps) => {
  const localization = useLocalizationContext();

  return (
    <ErrorBoundary>
      {header ? <Header {...header} /> : null}
      <AnnouncementBanner />
      <main id="page-content">
        <PressCoverContainer {...sidekick(sidekickLookup)}>
          <Typography variant="h1" align="center" sx={{ color: 'secondary.contrastText' }}>
            {localization['press.coverHeaderLabel']?.shortTextValue ?? 'Press Release'}
          </Typography>
        </PressCoverContainer>

        <Root maxWidth={'lg'} {...sidekick(sidekickLookup)}>
          <Box
            sx={{
              pb: 3.75,
            }}
          >
            <Title
              variant="h2"
              sx={{
                color: 'secondary.contrastText',
                pb: {
                  xs: 2,
                  md: 3,
                },
              }}
            >
              {title}
            </Title>

            {summary && (
              <StyledTextContainer>
                <Typography>{summary}</Typography>
              </StyledTextContainer>
            )}

            <Divider sx={{ my: { xs: 2.5, md: 5 } }} />

            {/* Each Activity boundary hydrates as its own task, as in PageGeneral. */}
            <Activity>
              <PressBody body={body} sidekickLookup={sidekickLookup} />
            </Activity>
          </Box>
        </Root>
      </main>
      {footer ? (
        <Activity>
          <Footer {...footer} />
        </Activity>
      ) : null}
    </ErrorBoundary>
  );
};

const PressBody = ({ sidekickLookup, body }: PressProps) => (
  <PressBodyRoot
    sx={{
      paddingTop: 2,
    }}
    {...sidekick(sidekickLookup)}
  >
    {body ? (
      <StyledTextContainer>
        <Text variant="blog" sidekickLookup={sidekickLookup?.body} body={body} />
      </StyledTextContainer>
    ) : null}

    <Box
      sx={{
        py: {
          xs: 1,
          md: 6,
        },
      }}
    >
      <Divider />
    </Box>
  </PressBodyRoot>
);

export default Press;

const PressCoverContainer = styled('header', {
  name: 'Press',
  slot: 'pressHeader',
})(({ theme }) => ({
  padding: theme.spacing(6.625, 0),
  backgroundColor: theme.palette.teal.main,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(13.25, 0),
  },
}));

const Root = styled(Container, {
  name: 'Press',
  slot: 'root',
})<{ variant?: string }>(({ theme }) => ({
  paddingTop: theme.spacing(3.375),
  paddingBottom: theme.spacing(3.375),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(10.75),
    paddingBottom: theme.spacing(10.75),
  },
}));

const StyledTextContainer = styled(Box, {
  name: 'Press',
  slot: 'text',
})(({ theme }) => ({
  '& p': {
    fontSize: '16px !important',
    [theme.breakpoints.up('md')]: {
      fontSize: '20px !important',
    },
  },
}));

const Title = styled(Typography, {
  name: 'Press',
  slot: 'title',
})(({ theme }) => ({
  fontSize: theme.typography.pxToRem(17),
  fontWeight: theme.typography.fontWeightBold,
  [theme.breakpoints.up('md')]: {
    fontSize: theme.typography.pxToRem(23),
  },
}));

const PressBodyRoot = styled(Grid, {
  name: 'Press',
  slot: 'pressBodyRoot',
})<{ variant?: string }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: 'auto',
  '& .box-with-embed': {
    paddingBottom: theme.spacing(2),
  },
  '& [class*="Text-root"] > [class*="Box-content"]:last-of-type': {
    justifyContent: 'center',
    width: '100%',
  },
  '& [class*="Text-root"] > [data-testid="Text-embedded-entry-block"]:last-of-type': {
    textAlign: 'center',
  },
}));
